import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import type { TrayTicketNotice } from '../../../types/trayTicket';

interface Props {
  notices: TrayTicketNotice[];
  open: boolean;
  onClose: () => void;
}

/**
 * Notices for the current resident + meal.
 *
 * Mirrors the WinForms `dpNotices` dock-panel on
 * frmDietech_Prd_TrayTicketVisualizer.vb. Two things changed:
 *
 * 1. The trigger. It used to be a 24px vertical tab of rotated text welded to
 *    the right edge — a docking-window idiom with no web equivalent, which
 *    also cost the ticket 28px of width at every viewport size because the
 *    scroll container beside it needed a matching margin to keep its scrollbar
 *    clear. It is now a counted button in the dialog's title bar.
 *
 * 2. Open state moved to the parent, because the badge needs the count anyway
 *    and two sources of truth for "is this open" is one too many.
 *
 * What did NOT change is that this is a panel inside the dialog rather than a
 * full-height app drawer. It opens itself when a fetch turns up real notices,
 * and a modal drawer that seizes the whole viewport on arrival would black out
 * the ticket the user came here to read. Overlaying the right third of the
 * tray is the most it should ever take.
 *
 * Row styling still matches WinForms `gvNotices_CustomDrawCell` (.vb:3668):
 * Header = navy bold, Footer = dark red italic, else blue bullet.
 */
export default function TrayTicketNoticesPanel({ notices, open, onClose }: Props) {
  return (
    <div
      role="region"
      aria-label="Notices"
      aria-hidden={!open}
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: 300,
        maxWidth: '85%',
        zIndex: 3,
        background: 'var(--card-bg, #fff)',
        borderLeft: '1px solid var(--border-color, #d1d5db)',
        boxShadow: open ? '-4px 0 16px rgba(0,0,0,0.10)' : 'none',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        // Keeps the panel out of the tab order and off the pointer while it is
        // parked off-screen; `transform` alone leaves it focusable.
        visibility: open ? 'visible' : 'hidden',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 4px 4px 12px',
          borderBottom: '1px solid var(--border-color, #d1d5db)',
          fontSize: 14,
          fontWeight: 600,
          color: 'var(--text-primary, #111827)',
        }}
      >
        <span>Notices</span>
        <IconButton onClick={onClose} aria-label="Close Notices panel" sx={{ width: 36, height: 36 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div style={{ overflowY: 'auto', padding: '8px 12px', flex: 1, minHeight: 0 }}>
        {notices.length === 0 ? (
          <div style={{ color: 'var(--text-secondary, #6b7280)', fontStyle: 'italic', fontSize: 14 }}>
            No notices for current meal.
          </div>
        ) : (
          notices.map((n, i) => {
            if (n.header) {
              return (
                <div
                  key={i}
                  style={{
                    color: '#000080',           // WinForms Color.Navy
                    fontWeight: 700,
                    fontSize: 14,
                    marginTop: i === 0 ? 0 : 6,
                    marginBottom: 2,
                  }}
                >
                  {n.description}
                </div>
              );
            }
            if (n.footer) {
              return (
                <div
                  key={i}
                  style={{
                    color: '#8B0000',           // WinForms Color.DarkRed
                    fontStyle: 'italic',
                    fontSize: 13,
                    marginTop: 2,
                    marginBottom: 2,
                  }}
                >
                  {n.description}
                </div>
              );
            }
            // Blank spacer.
            if (n.description === '') {
              return <div key={i} style={{ height: 6 }} />;
            }
            // Regular bullet — WinForms Color.FromArgb(49, 106, 197).
            return (
              <div
                key={i}
                style={{
                  color: 'rgb(49, 106, 197)',
                  fontSize: 14,
                  paddingLeft: 8,
                  lineHeight: 1.4,
                }}
              >
                {n.description}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * How many notices are worth flagging.
 *
 * The server always ships a row — when there is nothing to report it sends a
 * single "No notices for current meal." entry. Counting rows naively would put
 * a permanent `1` on the badge and train people to ignore it, so header,
 * footer, blank-spacer and that placeholder row are all excluded.
 */
export function countRealNotices(notices: TrayTicketNotice[]): number {
  return notices.filter(
    (n) => !n.header
      && !n.footer
      && n.description !== ''
      && n.description !== 'No notices for current meal.',
  ).length;
}
